// /var/www/oszczedzam/client/src/components/reports/ExpenseCategoryAnalysis.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Grid, Card, CardContent, Divider,
  List, ListItem, ListItemText, ListItemIcon, LinearProgress,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Tooltip, Button, Tabs, Tab, Stack, Chip, Avatar, Rating, IconButton,
  Alert, AlertTitle
} from '@mui/material';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, 
  Legend, Treemap, Sector, BarChart, Bar, XAxis, YAxis, CartesianGrid, Area, AreaChart,
  Line, LineChart, ComposedChart
} from 'recharts';
import { 
  TrendingDown as TrendingDownIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Warning as WarningIcon,
  Savings as SavingsIcon,
  ShowChart as ShowChartIcon,
  Money as MoneyIcon,
  Info as InfoIcon,
  LocalAtm as LocalAtmIcon,
  TrendingUp as TrendingUpIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
  Check as CheckIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

// Kolory do wykresu - bardziej przyjazna paleta
const COLORS = [
  '#4CAF50', '#2196F3', '#FF9800', '#F44336', '#9C27B0', 
  '#3F51B5', '#00BCD4', '#009688', '#FFEB3B', '#FF5722',
  '#673AB7', '#E91E63', '#8BC34A', '#CDDC39', '#607D8B',
  '#795548', '#4DB6AC', '#5C6BC0', '#FFA726', '#EF5350'
];

// Typy wykresów
const chartTypes = [
  { value: "pie", label: "Wykres kołowy" },
  { value: "bar", label: "Wykres słupkowy" },
  { value: "treemap", label: "Mapa drzewa" },
  { value: "area", label: "Wykres obszarowy" }
];

function ExpenseCategoryAnalysis({ transactions = [], allTransactions = [], filters = {}, budgets = [] }) {
  const theme = useTheme();
  const [pieData, setPieData] = useState([]);
  const [topExpenses, setTopExpenses] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [anomalies, setAnomalies] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [chartType, setChartType] = useState("pie");
  const [activeIndex, setActiveIndex] = useState(0);
  const [expenseInsights, setExpenseInsights] = useState(null);
  const [dailySpending, setDailySpending] = useState([]);
  const [categoryTrends, setCategoryTrends] = useState([]);
  const [financialHealth, setFinancialHealth] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      analyzeTransactions();
    } else {
      // Resetuj stany, gdy nie ma transakcji
      setPieData([]);
      setTopExpenses([]);
      setCategoryStats([]);
      setTotalExpenses(0);
      setAnomalies([]);
      setDailySpending([]);
      setCategoryTrends([]);
      setExpenseInsights(null);
      setFinancialHealth(null);
      setComparisonData(null);
    }
  }, [transactions, budgets]);

  // Analizuj transakcje i przygotuj dane do wyświetlenia
  const analyzeTransactions = () => {
    // Sprawdź czy mamy transakcje i budżety
    if (!transactions || transactions.length === 0) {
      setPieData([]);
      setTopExpenses([]);
      setCategoryStats([]);
      setAnomalies([]);
      setDailySpending([]);
      setCategoryTrends([]);
      setExpenseInsights(null);
      setFinancialHealth(null);
      setComparisonData(null);
      return;
    }

    // Filtruj wydatki (kwoty ujemne)
    const expenses = transactions.filter(t => t && t.amount < 0);
    
    // Oblicz sumę wydatków
    const total = expenses.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    setTotalExpenses(total);
    
    // Pogrupuj wydatki według kategorii
    const categoriesMap = {};
    expenses.forEach(transaction => {
      const category = transaction.category || 'Inne';
      const amount = Math.abs(transaction.amount);
      
      if (categoriesMap[category]) {
        categoriesMap[category].total += amount;
        categoriesMap[category].transactions.push(transaction);
      } else {
        categoriesMap[category] = { 
          total: amount, 
          transactions: [transaction],
          name: category 
        };
      }
    });
    
    // Przekształć w tablicę i dodaj procentowy udział
    const categoriesArray = Object.values(categoriesMap).map(category => ({
      ...category,
      percentage: total > 0 ? (category.total / total) * 100 : 0
    }));
    
    // Sortuj od największych wydatków
    categoriesArray.sort((a, b) => b.total - a.total);
    
    // Przygotuj dane do wykresu kołowego
    const pieChartData = categoriesArray.map(category => ({
      name: category.name,
      value: category.total,
      percentage: category.percentage.toFixed(1)
    }));
    
    // Przygotuj listę największych wydatków
    const topExpensesData = [...expenses]
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
      .slice(0, 10)
      .map(t => ({
        id: t.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
        title: t.title || 'Bez nazwy',
        category: t.category || 'Inne',
        amount: Math.abs(t.amount),
        date: t.date
      }));
    
    // Przygotuj porównanie z budżetami
    const categoryBudgetComparison = categoriesArray.map(category => {
      const budget = budgets && budgets.length > 0 
        ? budgets.find(b => b && b.category === category.name)
        : null;
      let budgetLimit = 0;
      let budgetPercentage = 0;
      
      if (budget) {
        // Dostosuj limit budżetu do wybranego okresu w filtrach
        budgetLimit = adjustBudgetToSelectedPeriod(budget);
        budgetPercentage = budgetLimit > 0 ? (category.total / budgetLimit) * 100 : 0;
      }
      
      return {
        ...category,
        budgetLimit,
        budgetPercentage,
        overBudget: budgetLimit > 0 && category.total > budgetLimit,
        remaining: budgetLimit > 0 ? budgetLimit - category.total : 0
      };
    });
    
    // Wykryj anomalie w wydatkach
    const anomaliesData = detectAnomalies(categoriesArray, allTransactions || [], filters || {});
    
    // Przygotuj analizę dziennych wydatków
    const dailySpendingData = calculateDailySpending(expenses);
    
    // Przygotuj trendy kategorii w czasie
    const categoryTrendsData = calculateCategoryTrends(allTransactions || [], categoriesArray);
    
    // Generuj wnioski i rekomendacje
    const insightsData = generateInsights(categoryBudgetComparison, anomaliesData, total);
    
    // Ocena ogólnego zdrowia finansowego
    const healthData = calculateFinancialHealth(categoryBudgetComparison, total);
    
    // Przygotuj dane porównawcze
    const comparisonData = prepareComparisonData(allTransactions || [], filters || {});
    
    // Ustaw dane w stanie
    setPieData(pieChartData);
    setTopExpenses(topExpensesData);
    setCategoryStats(categoryBudgetComparison);
    setAnomalies(anomaliesData);
    setDailySpending(dailySpendingData);
    setCategoryTrends(categoryTrendsData);
    setExpenseInsights(insightsData);
    setFinancialHealth(healthData);
    setComparisonData(comparisonData);
  };

  // Przygotuj dane porównawcze z poprzednim okresem
  const prepareComparisonData = (allTransactions, filters) => {
    if (!allTransactions || !filters) {
      return null;
    }
    
    const { period, year, month } = filters;
    
    // Określ daty bieżącego okresu
    let currentPeriodStart, currentPeriodEnd;
    let previousPeriodStart, previousPeriodEnd;
    
    const currentDate = new Date();
    
    if (period === 'month') {
      // Bieżący miesiąc
      currentPeriodStart = new Date(year, month, 1);
      currentPeriodEnd = new Date(year, month + 1, 0);
      
      // Poprzedni miesiąc
      previousPeriodStart = new Date(year, month - 1, 1);
      previousPeriodEnd = new Date(year, month, 0);
      
      // Korekta dla stycznia
      if (month === 0) {
        previousPeriodStart = new Date(year - 1, 11, 1);
        previousPeriodEnd = new Date(year, 0, 0);
      }
    } else if (period === 'year') {
      // Bieżący rok
      currentPeriodStart = new Date(year, 0, 1);
      currentPeriodEnd = new Date(year, 11, 31);
      
      // Poprzedni rok
      previousPeriodStart = new Date(year - 1, 0, 1);
      previousPeriodEnd = new Date(year - 1, 11, 31);
    } else {
      // Domyślnie ostatnie 30 dni
      currentPeriodEnd = new Date();
      currentPeriodStart = new Date();
      currentPeriodStart.setDate(currentPeriodStart.getDate() - 30);
      
      previousPeriodEnd = new Date(currentPeriodStart);
      previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
      previousPeriodStart = new Date(previousPeriodEnd);
      previousPeriodStart.setDate(previousPeriodStart.getDate() - 30);
    }
    
    // Filtruj transakcje dla bieżącego okresu
    const currentPeriodTransactions = allTransactions.filter(transaction => {
      if (!transaction || !transaction.date) return false;
      const date = new Date(transaction.date);
      return date >= currentPeriodStart && date <= currentPeriodEnd && transaction.amount < 0;
    });
    
    // Filtruj transakcje dla poprzedniego okresu
    const previousPeriodTransactions = allTransactions.filter(transaction => {
      if (!transaction || !transaction.date) return false;
      const date = new Date(transaction.date);
      return date >= previousPeriodStart && date <= previousPeriodEnd && transaction.amount < 0;
    });
    
    // Oblicz sumy wydatków
    const currentTotal = currentPeriodTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const previousTotal = previousPeriodTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    // Grupuj wydatki według kategorii dla obu okresów
    const categoriesComparison = [];
    const allCategories = new Set();
    
    // Zbierz wszystkie kategorie
    currentPeriodTransactions.forEach(t => allCategories.add(t.category || 'Inne'));
    previousPeriodTransactions.forEach(t => allCategories.add(t.category || 'Inne'));
    
    // Oblicz wydatki dla każdej kategorii w obu okresach
    allCategories.forEach(category => {
      const currentAmount = currentPeriodTransactions
        .filter(t => (t.category || 'Inne') === category)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      const previousAmount = previousPeriodTransactions
        .filter(t => (t.category || 'Inne') === category)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      const change = previousAmount > 0 
        ? ((currentAmount - previousAmount) / previousAmount) * 100 
        : currentAmount > 0 ? 100 : 0;
      
      categoriesComparison.push({
        category,
        current: currentAmount,
        previous: previousAmount,
        change
      });
    });
    
    // Sortuj według różnicy między okresami (malejąco)
    categoriesComparison.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
    
    // Przygotuj dane dla wykresów porównawczych
    const barChartData = categoriesComparison
      .filter(cat => cat.current > 0 || cat.previous > 0)
      .slice(0, 8)
      .map(cat => ({
        name: cat.category,
        obecny: cat.current,
        poprzedni: cat.previous,
        zmiana: cat.change.toFixed(1) + '%'
      }));
    
    return {
      currentTotal,
      previousTotal,
      change: previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0,
      categoriesComparison,
      barChartData,
      periodName: period === 'month' 
        ? `${month + 1}/${year}` 
        : period === 'year' ? `${year}` : 'ostatnie 30 dni',
      previousPeriodName: period === 'month' 
        ? `${(month === 0 ? 12 : month)}/${month === 0 ? year - 1 : year}` 
        : period === 'year' ? `${year - 1}` : 'poprzednie 30 dni'
    };
  };

  // Ocena ogólnego zdrowia finansowego
  const calculateFinancialHealth = (categoryBudgetComparison, totalExpenses) => {
    if (!categoryBudgetComparison || categoryBudgetComparison.length === 0) {
      return null;
    }
    
    // Liczba przekroczonych budżetów
    const budgetedCategories = categoryBudgetComparison.filter(cat => cat.budgetLimit > 0);
    const overBudgetCategories = budgetedCategories.filter(cat => cat.overBudget);
    
    // Procent budżetów przekroczonych
    const budgetOvershootPercentage = budgetedCategories.length > 0 
      ? (overBudgetCategories.length / budgetedCategories.length) * 100 
      : 0;
    
    // Suma przekroczeń budżetów
    const totalOverBudget = overBudgetCategories.reduce((sum, cat) => 
      sum + (cat.total - cat.budgetLimit), 0);
    
    // Procent wydatków poza budżetem
    const overBudgetPercentage = totalExpenses > 0 
      ? (totalOverBudget / totalExpenses) * 100 
      : 0;
    
    // Ocena zdrowia finansowego (1-5)
    let healthScore = 5;
    let healthStatus = 'Doskonały';
    let recommendations = [];
    
    if (budgetOvershootPercentage > 50 || overBudgetPercentage > 25) {
      healthScore = 1;
      healthStatus = 'Krytyczny';
      recommendations = [
        'Natychmiast zrewiduj swoje wydatki we wszystkich kategoriach',
        'Skup się na ograniczeniu wydatków w najbardziej przekroczonych kategoriach',
        'Rozważ tymczasowe cięcia we wszystkich kategoriach poza niezbędnymi'
      ];
    } else if (budgetOvershootPercentage > 30 || overBudgetPercentage > 15) {
      healthScore = 2;
      healthStatus = 'Zły';
      recommendations = [
        'Szczegółowo przeanalizuj przekroczone kategorie',
        'Stwórz plan naprawczy dla kategorii z największymi przekroczeniami',
        'Unikaj dodatkowych wydatków do końca okresu'
      ];
    } else if (budgetOvershootPercentage > 20 || overBudgetPercentage > 10) {
      healthScore = 3;
      healthStatus = 'Przeciętny';
      recommendations = [
        'Monitoruj przekroczone kategorie',
        'Ogranicz wydatki uznaniowe',
        'Rozważ dostosowanie budżetów w problematycznych kategoriach'
      ];
    } else if (budgetOvershootPercentage > 10 || overBudgetPercentage > 5) {
      healthScore = 4;
      healthStatus = 'Dobry';
      recommendations = [
        'Kontynuuj monitorowanie wydatków',
        'Zwiększ oszczędności',
        'Rozważ optymalizację w kategoriach z niewielkimi przekroczeniami'
      ];
    } else {
      healthScore = 5;
      healthStatus = 'Doskonały';
      recommendations = [
        'Utrzymuj bieżące nawyki finansowe',
        'Rozważ zwiększenie celów oszczędnościowych',
        'Przeanalizuj możliwości inwestycyjne'
      ];
    }
    
    return {
      score: healthScore,
      status: healthStatus,
      overBudgetCategories: overBudgetCategories.length,
      totalCategories: budgetedCategories.length,
      budgetOvershootPercentage,
      totalOverBudget,
      overBudgetPercentage,
      recommendations
    };
  };

  // Generuj wnioski i rekomendacje
  const generateInsights = (categoryBudgetComparison, anomaliesData, totalExpenses) => {
    if (!categoryBudgetComparison || categoryBudgetComparison.length === 0) {
      return null;
    }
    
    const insights = [];
    
    // Brak budżetów dla większości kategorii
    const categoriesWithBudget = categoryBudgetComparison.filter(cat => cat.budgetLimit > 0);
    if (categoriesWithBudget.length === 0) {
      insights.push({
        type: 'suggestion',
        title: 'Brak budżetów',
        message: 'Nie masz ustawionego żadnego budżetu. Ustaw budżety dla swoich głównych kategorii wydatków, aby lepiej zarządzać finansami.',
        priority: 'high'
      });
    } else if (categoriesWithBudget.length / categoryBudgetComparison.length < 0.5) {
      insights.push({
        type: 'suggestion',
        title: 'Mało budżetów',
        message: `Tylko ${categoriesWithBudget.length} z ${categoryBudgetComparison.length} kategorii ma ustawiony budżet. Rozważ dodanie budżetów dla pozostałych kategorii.`,
        priority: 'medium'
      });
    }
    
    // Główne kategorie wydatków
    const topCategories = [...categoryBudgetComparison].sort((a, b) => b.total - a.total);
    if (topCategories.length > 0) {
      // Sprawdź dominujący wydatek
      const topCategory = topCategories[0];
      if (topCategory.percentage > 50) {
        insights.push({
          type: 'warning',
          title: 'Dominujący wydatek',
          message: `Kategoria "${topCategory.name}" stanowi ponad 50% wszystkich Twoich wydatków (${topCategory.percentage.toFixed(1)}%). Rozważ bardziej zbalansowany budżet.`,
          priority: 'high'
        });
      }
      
      // Top 3 kategorie
      const top3 = topCategories.slice(0, 3);
      const top3Percentage = top3.reduce((sum, cat) => sum + cat.percentage, 0);
      insights.push({
        type: 'info',
        title: 'Główne kategorie wydatków',
        message: `Największe wydatki: ${top3.map(cat => cat.name).join(', ')} stanowią łącznie ${top3Percentage.toFixed(1)}% Twoich wydatków.`,
        priority: 'low'
      });
    }
    
    // Przekroczone budżety
    const overBudget = categoryBudgetComparison.filter(cat => cat.overBudget);
    if (overBudget.length > 0) {
      overBudget.sort((a, b) => (b.total - b.budgetLimit) - (a.total - a.budgetLimit));
      
      // Największe przekroczenie
      const worstOvershoot = overBudget[0];
      const overshootAmount = worstOvershoot.total - worstOvershoot.budgetLimit;
      const overshootPercentage = (overshootAmount / worstOvershoot.budgetLimit) * 100;
      
      insights.push({
        type: 'warning',
        title: 'Przekroczony budżet',
        message: `Największe przekroczenie budżetu w kategorii "${worstOvershoot.name}" - o ${formatAmount(overshootAmount)} (${overshootPercentage.toFixed(1)}%).`,
        priority: 'high'
      });
      
      if (overBudget.length > 1) {
        insights.push({
          type: 'warning',
          title: 'Wiele przekroczeń budżetu',
          message: `Przekroczyłeś budżet w ${overBudget.length} kategoriach. Sumaryczne przekroczenie: ${formatAmount(overBudget.reduce((sum, cat) => sum + (cat.total - cat.budgetLimit), 0))}.`,
          priority: 'medium'
        });
      }
    }
    
    // Oszczędności vs budżet
    const underBudget = categoryBudgetComparison.filter(cat => cat.budgetLimit > 0 && !cat.overBudget);
    if (underBudget.length > 0) {
      const totalSavings = underBudget.reduce((sum, cat) => sum + (cat.budgetLimit - cat.total), 0);
      const savingsPercentage = (totalSavings / totalExpenses) * 100;
      
      if (savingsPercentage > 20) {
        insights.push({
          type: 'success',
          title: 'Znaczne oszczędności',
          message: `Twoje wydatki są niższe od budżetu o ${formatAmount(totalSavings)} (${savingsPercentage.toFixed(1)}%). Świetna robota!`,
          priority: 'medium'
        });
      } else if (savingsPercentage > 5) {
        insights.push({
          type: 'success',
          title: 'Oszczędności',
          message: `Udało Ci się zostać poniżej budżetu o ${formatAmount(totalSavings)} (${savingsPercentage.toFixed(1)}%).`,
          priority: 'low'
        });
      }
    }
    
    // Anomalie
    if (anomaliesData && anomaliesData.length > 0) {
      const topAnomaly = anomaliesData[0];
      insights.push({
        type: 'warning',
        title: 'Wykryto anomalię wydatków',
        message: `Kategoria "${topAnomaly.category}" ma wyjątkowo wysokie wydatki w porównaniu do historii (o ${topAnomaly.percentage.toFixed(1)}% więcej).`,
        priority: 'high'
      });
    }
    
    // Dziury w budżecie (kategorie bez wydatków)
    const categoriesWithoutExpenses = categoryBudgetComparison
      .filter(cat => cat.total === 0 && cat.budgetLimit > 0);
    
    if (categoriesWithoutExpenses.length > 0) {
      insights.push({
        type: 'info',
        title: 'Niewykorzystane budżety',
        message: `Masz ${categoriesWithoutExpenses.length} kategorii z ustawionym budżetem, ale bez wydatków w tym okresie.`,
        priority: 'low'
      });
    }
    
    // Sortowanie według priorytetu
    const priorityOrder = { 'high': 0, 'medium': 1, 'low': 2 };
    insights.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
    
    return insights;
  };
  
  // Oblicz trendy kategorii w czasie
  const calculateCategoryTrends = (allTransactions, currentCategories) => {
    if (!allTransactions || !currentCategories || allTransactions.length === 0 || currentCategories.length === 0) {
      return { monthlyData: [], categories: [] };
    }
    
    const categoryNames = currentCategories.map(cat => cat.name);
    const last6Months = [];
    
    // Przygotuj dane dla ostatnich 6 miesięcy
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // Nazwy miesięcy
    const monthNames = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
    
    // Oblicz dane dla każdego miesiąca
    for (let i = 5; i >= 0; i--) {
      let monthIndex = currentMonth - i;
      let year = currentYear;
      
      if (monthIndex < 0) {
        monthIndex += 12;
        year -= 1;
      }
      
      const monthStart = new Date(year, monthIndex, 1);
      const monthEnd = new Date(year, monthIndex + 1, 0);
      
      // Filtruj transakcje dla tego miesiąca
      const monthTransactions = allTransactions.filter(transaction => {
        if (!transaction || !transaction.date) return false;
        const date = new Date(transaction.date);
        return date >= monthStart && date <= monthEnd && transaction.amount < 0;
      });
      
      // Oblicz sumy dla każdej kategorii
      const monthlyCategoryData = { 
        name: monthNames[monthIndex],
        month: monthIndex,
        year: year
      };
      
      categoryNames.forEach(category => {
        monthlyCategoryData[category] = monthTransactions
          .filter(t => (t.category || 'Inne') === category)
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      });
      
      last6Months.push(monthlyCategoryData);
    }
    
    // Wybierz tylko top 5 kategorii do wykresu trendów
    const top5Categories = categoryNames.slice(0, 5);
    
    return {
      monthlyData: last6Months,
      categories: top5Categories
    };
  };
  
  // Oblicz dzienne wydatki
  const calculateDailySpending = (expenses) => {
    if (!expenses || expenses.length === 0) {
      return [];
    }
    
    const daysMap = {};
    
    // Pogrupuj wydatki według dni
    expenses.forEach(transaction => {
      if (!transaction || !transaction.date) return;
      const date = transaction.date.split('T')[0]; // Format YYYY-MM-DD
      const amount = Math.abs(transaction.amount);
      
      if (daysMap[date]) {
        daysMap[date] += amount;
      } else {
        daysMap[date] = amount;
      }
    });
    
    // Przekształć w tablicę
    const dailyData = Object.entries(daysMap).map(([date, amount]) => ({
      date,
      amount
    }));
    
    // Sortuj według daty
    dailyData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Oblicz 7-dniową średnią kroczącą
    const movingAverage = calculateMovingAverage(dailyData, 7);
    
    // Dodaj średnią do danych
    const finalData = dailyData.map((day, index) => ({
      ...day,
      avg: movingAverage[index] || null,
      formattedDate: formatDate(day.date)
    }));
    
    return finalData;
  };
  
  // Oblicz średnią kroczącą
  const calculateMovingAverage = (data, window) => {
    if (!data || data.length === 0) {
      return [];
    }
    
    const result = [];
    
    for (let i = 0; i < data.length; i++) {
      if (i < window - 1) {
        // Niewystarczająco punktów do obliczenia średniej na początku
        result.push(null);
      } else {
        // Oblicz średnią z okna
        let sum = 0;
        for (let j = 0; j < window; j++) {
          sum += data[i - j].amount;
        }
        result.push(sum / window);
      }
    }
    
    return result;
  };

  // Dostosuj limit budżetu do wybranego okresu
  const adjustBudgetToSelectedPeriod = (budget) => {
    if (!budget || !filters) {
      return 0;
    }
    
    const { period } = filters;
    
    if (budget.period === 'monthly' && period === 'month') {
      return budget.limit;
    } else if (budget.period === 'monthly' && period === 'year') {
      return budget.limit * 12;
    } else if (budget.period === 'yearly' && period === 'month') {
      return budget.limit / 12;
    } else if (budget.period === 'yearly' && period === 'year') {
      return budget.limit;
    } else if (budget.period === 'weekly' && period === 'month') {
      return budget.limit * 4.33; // Średnia liczba tygodni w miesiącu
    } else if (budget.period === 'weekly' && period === 'year') {
      return budget.limit * 52;
    }
    
    return budget.limit;
  };

  // Wykryj anomalie w wydatkach
  const detectAnomalies = (categories, allTransactions, filters) => {
    if (!categories || !allTransactions || !filters || categories.length === 0 || allTransactions.length === 0) {
      return [];
    }
    
    const anomalies = [];
    const { period, year, month } = filters;
    
    // Oblicz średnie miesięczne wydatki dla każdej kategorii z ostatnich 6 miesięcy
    categories.forEach(category => {
      const categoryTransactions = allTransactions.filter(t => 
        t && t.category === category.name && t.amount < 0
      );
      
      // Zbierz dane historyczne z ostatnich 6 miesięcy
      const historicalData = [];
      for (let i = 1; i <= 6; i++) {
        let historicalMonth = month - i;
        let historicalYear = year;
        
        while (historicalMonth < 0) {
          historicalMonth += 12;
          historicalYear -= 1;
        }
        
        const monthlyExpenses = categoryTransactions
          .filter(t => {
            if (!t || !t.date) return false;
            const date = new Date(t.date);
            return date.getFullYear() === historicalYear && date.getMonth() === historicalMonth;
          })
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);
        
        if (monthlyExpenses > 0) {
          historicalData.push(monthlyExpenses);
        }
      }
      
      // Jeśli mamy wystarczająco danych historycznych, sprawdź anomalie
      if (historicalData.length >= 3) {
        const average = historicalData.reduce((sum, amount) => sum + amount, 0) / historicalData.length;
        const stdDev = Math.sqrt(
          historicalData.reduce((sum, amount) => sum + Math.pow(amount - average, 2), 0) / historicalData.length
        );
        
        // Uznaj wydatki za anomalne, jeśli są 2 odchylenia standardowe od średniej
        const threshold = average + (2 * stdDev);
        
        if (category.total > threshold && category.total > average * 1.5) {
          anomalies.push({
            category: category.name,
            current: category.total,
            average,
            percentage: ((category.total - average) / average) * 100,
            threshold
          });
        }
      }
    });
    
    return anomalies.sort((a, b) => b.percentage - a.percentage);
  };

  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Formatuj datę
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('pl-PL', options);
    } catch (error) {
      console.error('Błąd formatowania daty:', error);
      return dateString;
    }
  };
  
  // Formatuj procent
  const formatPercentage = (percentage) => {
    return percentage.toFixed(1) + '%';
  };

  // Custom tooltip dla wykresu kołowego
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper elevation={3} sx={{ p: 1.5, backgroundColor: 'white' }}>
          <Typography variant="body2"><strong>{data.name}</strong></Typography>
          <Typography variant="body2">
            {formatAmount(data.value)} ({data.percentage}%)
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Renderuj aktywny sektor wykresu kołowego
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;
    
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 12}
          outerRadius={outerRadius + 16}
          fill={fill}
        />
        <text x={cx} y={cy} textAnchor="middle" fill={theme.palette.text.primary} fontSize="1rem" fontWeight="bold">
          {payload.name}
        </text>
        <text x={cx} y={cy + 20} textAnchor="middle" fill={theme.palette.text.secondary}>
          {formatAmount(value)}
        </text>
      </g>
    );
  };

  // Funkcja dla aktywnego segmentu wykresu
  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  // Obsługa zmiany zakładki
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Obsługa zmiany typu wykresu
  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  // Renderowanie różnych typów wykresów
  const renderChart = () => {
    if (!pieData || pieData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 300 }}>
          <Alert severity="info">Brak danych do wyświetlenia wykresu</Alert>
        </Box>
      );
    }

    switch (chartType) {
      case "pie":
        return (
          <ResponsiveContainer width="100%" height="100%" minHeight={400}>
            <PieChart>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={110}
                paddingAngle={1}
                dataKey="value"
                onMouseEnter={onPieEnter}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomPieTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height="100%" minHeight={400}>
            <BarChart
              data={pieData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={70} />
              <YAxis />
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Legend />
              <Bar dataKey="value" fill={theme.palette.primary.main}>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case "treemap":
        return (
          <ResponsiveContainer width="100%" height="100%" minHeight={400}>
            <Treemap
              data={pieData}
              dataKey="value"
              nameKey="name"
              aspectRatio={4/3}
              stroke="#fff"
              fill={theme.palette.primary.main}
              content={renderCustomizedTreemap}
            >
              <RechartsTooltip
                formatter={(value) => formatAmount(value)}
                labelFormatter={(label) => label}
                cursor={{ stroke: theme.palette.primary.dark, strokeWidth: 2, fillOpacity: 0.1 }}
              />
            </Treemap>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height="100%" minHeight={400}>
            <AreaChart
              data={pieData}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={70} />
              <YAxis />
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Legend />
              <Area type="monotone" dataKey="value" stroke={theme.palette.primary.main} fill={theme.palette.primary.light} />
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  // Renderowanie niestandardowego elementu treemap
  const renderCustomizedTreemap = (props) => {
    const { x, y, width, height, name, value, index } = props;

    const showCenteredLabel = width >= 80 && height >= 60;
    const showCompactLabel = !showCenteredLabel && width >= 36 && height >= 28;
    const fillColor = COLORS[index % COLORS.length];

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill: fillColor,
            stroke: '#fff',
            strokeWidth: 2,
            fillOpacity: 0.88,
          }}
        />
        <title>{`${name}: ${formatAmount(value)}`}</title>
        {showCenteredLabel && (
          <>
            <text
              x={x + width / 2}
              y={y + height / 2 - 8}
              textAnchor="middle"
              fill="#fff"
              fontSize={Math.max(10, Math.min(16, width / 8))}
              fontWeight="bold"
            >
              {name}
            </text>
            <text
              x={x + width / 2}
              y={y + height / 2 + 12}
              textAnchor="middle"
              fill="#fff"
              fontSize={Math.max(9, Math.min(14, width / 10))}
            >
              {formatAmount(value)}
            </text>
          </>
        )}
        {showCompactLabel && (
          <text
            x={x + 6}
            y={y + 16}
            textAnchor="start"
            fill="#fff"
            fontSize={9}
            fontWeight="600"
          >
            {name.length > 10 ? `${name.slice(0, 9)}…` : name}
          </text>
        )}
      </g>
    );
  };
  
  // Renderowanie wskaźnika zdrowia finansowego
  const renderFinancialHealth = () => {
    if (!financialHealth) return null;
    
    const { score, status, recommendations } = financialHealth;
    
    const scoreColors = {
      1: theme.palette.error.main,
      2: theme.palette.error.light,
      3: theme.palette.warning.main,
      4: theme.palette.success.light,
      5: theme.palette.success.main
    };
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Zdrowie finansowe</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              variant="outlined" 
              sx={{ 
                position: 'relative', 
                overflow: 'hidden',
                border: `1px solid ${scoreColors[score]}`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: scoreColors[score]
                }
              }}
            >
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  Ocena zdrowia finansowego
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h4" component="div" sx={{ mr: 2 }}>
                    {status}
                  </Typography>
                  <Rating 
                    value={score} 
                    readOnly 
                    max={5}
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: scoreColors[score]
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {score >= 4 
                    ? 'Twoje finanse są w dobrej kondycji, kontynuuj dobre praktyki.'
                    : score >= 3 
                      ? 'Wymaga uwagi w niektórych obszarach, ale ogólnie jest stabilnie.'
                      : 'Wymagane natychmiastowe działania naprawcze.'
                  }
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={9}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  Rekomendacje
                </Typography>
                <List dense>
                  {recommendations.map((rec, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  // Renderowanie kart wniosków
  const renderInsightCards = () => {
    if (!expenseInsights || expenseInsights.length === 0) return null;
    
    const iconMap = {
      'warning': <WarningIcon fontSize="small" color="error" />,
      'success': <SavingsIcon fontSize="small" color="success" />,
      'info': <InfoIcon fontSize="small" color="info" />,
      'suggestion': <LocalAtmIcon fontSize="small" color="primary" />
    };
    
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>Analiza i wnioski</Typography>
        <Grid container spacing={2}>
          {expenseInsights.map((insight, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Alert 
                severity={insight.type === 'warning' 
                  ? 'error' 
                  : insight.type === 'success' 
                    ? 'success' 
                    : insight.type === 'info' 
                      ? 'info' 
                      : 'info'
                } 
                icon={iconMap[insight.type]}
                sx={{ 
                  height: '100%', 
                  '.MuiAlert-message': { width: '100%' } 
                }}
              >
                <AlertTitle>{insight.title}</AlertTitle>
                {insight.message}
              </Alert>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };
  
  // Renderowanie porównania z poprzednim okresem
  const renderComparison = () => {
    if (!comparisonData) return null;
    
    const percentChange = comparisonData.change;
    const isPositive = percentChange <= 0; // Dla wydatków, spadek jest pozytywny
    
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>Porównanie z poprzednim okresem</Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  Obecny okres: {comparisonData.periodName}
                </Typography>
                <Typography variant="h5" component="div" color="error.main">
                  {formatAmount(comparisonData.currentTotal)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  Poprzedni okres: {comparisonData.previousPeriodName}
                </Typography>
                <Typography variant="h5" component="div" color="error.main">
                  {formatAmount(comparisonData.previousTotal)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card 
              variant="outlined" 
              sx={{ 
                border: `1px solid ${isPositive ? theme.palette.success.main : theme.palette.error.main}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  backgroundColor: isPositive ? theme.palette.success.main : theme.palette.error.main
                }
              }}
            >
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  Zmiana wydatków
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="h5" 
                    component="div" 
                    color={isPositive ? 'success.main' : 'error.main'} 
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {isPositive 
                      ? <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} /> 
                      : <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                    }
                    {Math.abs(percentChange).toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {isPositive 
                    ? `Twoje wydatki spadły o ${formatAmount(comparisonData.previousTotal - comparisonData.currentTotal)}`
                    : `Twoje wydatki wzrosły o ${formatAmount(comparisonData.currentTotal - comparisonData.previousTotal)}`
                  }
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Typography variant="subtitle1" gutterBottom>Porównanie kategorii</Typography>
        <Box sx={{ height: 350, mb: 3 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={comparisonData.barChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={70} />
              <YAxis />
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Legend />
              <Bar name="Obecny okres" dataKey="obecny" fill={theme.palette.primary.main} />
              <Bar name="Poprzedni okres" dataKey="poprzedni" fill={theme.palette.secondary.main} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        
        <Typography variant="subtitle1" gutterBottom>Największe zmiany</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kategoria</TableCell>
                <TableCell align="right">Obecny</TableCell>
                <TableCell align="right">Poprzedni</TableCell>
                <TableCell align="right">Zmiana</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonData.categoriesComparison
                .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
                .slice(0, 5)
                .map((category) => (
                  <TableRow key={category.category}>
                    <TableCell>{category.category}</TableCell>
                    <TableCell align="right">{formatAmount(category.current)}</TableCell>
                    <TableCell align="right">{formatAmount(category.previous)}</TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        color: category.change <= 0 ? 'success.main' : 'error.main',
                        fontWeight: 'medium',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                      }}
                    >
                      {category.change <= 0 
                        ? <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                        : <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                      }
                      {Math.abs(category.change).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        {/* Zakładki */}
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Przegląd wydatków" />
          <Tab label="Porównanie" />
          <Tab label="Trendy" />
          <Tab label="Analiza i rekomendacje" />
        </Tabs>
        
        {/* Zawartość zakładki Przegląd wydatków */}
        {tabValue === 0 && (
          <>
            {pieData && pieData.length > 0 ? (
              <>
                {/* Podsumowanie wydatków */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Podsumowanie wydatków: <strong>{formatAmount(totalExpenses)}</strong>
                  </Typography>
                  
                  {/* Wybór typu wykresu */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {chartTypes.map(type => (
                      <Chip 
                        key={type.value}
                        label={type.label}
                        onClick={() => handleChartTypeChange(type.value)}
                        color={chartType === type.value ? "primary" : "default"}
                        variant={chartType === type.value ? "filled" : "outlined"}
                      />
                    ))}
                  </Box>
                  
                  {/* Wykres kategorii */}
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ height: 400 }}>
                        {renderChart()}
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Kategoria</TableCell>
                              <TableCell align="right">Kwota</TableCell>
                              <TableCell align="right">Udział</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pieData.map((category, index) => (
                              <TableRow 
                                key={category.name}
                                sx={{ 
                                  backgroundColor: index === activeIndex 
                                    ? alpha(COLORS[index % COLORS.length], 0.1) 
                                    : 'transparent',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: alpha(COLORS[index % COLORS.length], 0.1)
                                  }
                                }}
                                onClick={() => setActiveIndex(index)}
                              >
                                <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      borderRadius: '50%', 
                                      backgroundColor: COLORS[index % COLORS.length],
                                      mr: 1
                                    }} 
                                  />
                                  {category.name}
                                </TableCell>
                                <TableCell align="right">{formatAmount(category.value)}</TableCell>
                                <TableCell align="right">{category.percentage}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Porównanie z budżetem */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>Wykorzystanie budżetu</Typography>
                  {categoryStats && categoryStats.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Kategoria</TableCell>
                            <TableCell align="right">Wydatki</TableCell>
                            <TableCell align="right">Budżet</TableCell>
                            <TableCell align="right">Pozostało</TableCell>
                            <TableCell>Wykorzystanie</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryStats.map((category) => {
                            const hasBudget = category.budgetLimit > 0;
                            const isBudgetExceeded = hasBudget && category.total > category.budgetLimit;
                            const remainingBudget = hasBudget ? category.budgetLimit - category.total : 0;
                            const percentUsed = hasBudget ? (category.total / category.budgetLimit) * 100 : 0;
                            
                            // Określ kolor paska postępu na podstawie procentu wykorzystania
                            const progressColor = () => {
                              if (percentUsed >= 100) return 'error';
                              if (percentUsed >= 80) return 'warning';
                              return 'success';
                            };
                            
                            return (
                              <TableRow key={category.name}>
                                <TableCell>{category.name}</TableCell>
                                <TableCell align="right">{formatAmount(category.total)}</TableCell>
                                <TableCell align="right">
                                  {hasBudget ? formatAmount(category.budgetLimit) : "-"}
                                </TableCell>
                                <TableCell align="right">
                                  {hasBudget ? (
                                    <Box component="span" sx={{ 
                                      color: isBudgetExceeded ? 'error.main' : 'success.main',
                                      fontWeight: isBudgetExceeded ? 'bold' : 'normal'
                                    }}>
                                      {isBudgetExceeded ? '-' : ''}{formatAmount(Math.abs(remainingBudget))}
                                    </Box>
                                  ) : "-"}
                                </TableCell>
                                <TableCell>
                                  {hasBudget ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Box sx={{ width: '100%', mr: 1 }}>
                                        <LinearProgress 
                                          variant="determinate" 
                                          value={Math.min(percentUsed, 100)} 
                                          color={progressColor()}
                                          sx={{ height: 8, borderRadius: 4 }}
                                        />
                                      </Box>
                                      <Box sx={{ minWidth: 35 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          {percentUsed.toFixed(0)}%
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ) : "-"}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">Brak danych o budżetach</Alert>
                  )}
                </Box>
                
                {/* Największe wydatki */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Największe wydatki</Typography>
                    {topExpenses && topExpenses.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Tytuł</TableCell>
                              <TableCell>Kategoria</TableCell>
                              <TableCell>Data</TableCell>
                              <TableCell align="right">Kwota</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {topExpenses.map((expense) => (
                              <TableRow key={expense.id}>
                                <TableCell>{expense.title}</TableCell>
                                <TableCell>{expense.category}</TableCell>
                                <TableCell>{formatDate(expense.date)}</TableCell>
                                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                                  {formatAmount(expense.amount)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Brak danych o wydatkach</Alert>
                    )}
                  </Box>
                  
                  {/* Anomalie w wydatkach */}
                  {anomalies && anomalies.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Wykryte anomalie wydatków
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Poniższe kategorie mają znacznie wyższe wydatki niż zwykle:
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Kategoria</TableCell>
                                <TableCell align="right">Aktualne wydatki</TableCell>
                                <TableCell align="right">Średnia historyczna</TableCell>
                                <TableCell align="right">Odchylenie</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {anomalies.map((anomaly) => (
                                <TableRow key={anomaly.category}>
                                  <TableCell>{anomaly.category}</TableCell>
                                  <TableCell align="right">{formatAmount(anomaly.current)}</TableCell>
                                  <TableCell align="right">{formatAmount(anomaly.average)}</TableCell>
                                  <TableCell 
                                    align="right" 
                                    sx={{ color: 'error.main', fontWeight: 'medium' }}
                                  >
                                    +{formatPercentage(anomaly.percentage)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  Brak danych wydatków dla wybranego okresu. Wybierz inny okres lub dodaj transakcje.
                </Alert>
              )}
          </>
        )}
        
        {/* Zawartość zakładki Porównanie */}
        {tabValue === 1 && (
          <>
            {comparisonData ? (
              renderComparison()
            ) : (
              <Alert severity="info">
                Brak wystarczających danych do porównania. Potrzebujesz transakcji z co najmniej dwóch okresów.
              </Alert>
            )}
            {financialHealth ? (
              renderFinancialHealth()
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                Brak wystarczających danych do oceny zdrowia finansowego.
              </Alert>
            )}
          </>
        )}
        
        {/* Zawartość zakładki Trendy */}
        {tabValue === 2 && (
          <>
            {/* Dzienny wykres wydatków */}
            {dailySpending && dailySpending.length > 0 ? (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Dzienny rozkład wydatków</Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={dailySpending} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="formattedDate" 
                        angle={-45} 
                        textAnchor="end" 
                        height={60}
                        interval={Math.ceil(dailySpending.length / 15)}
                      />
                      <YAxis />
                      <RechartsTooltip formatter={(value) => formatAmount(value)} />
                      <Bar dataKey="amount" fill={theme.palette.primary.main} barSize={20} name="Wydatki" />
                      <Line dataKey="avg" stroke={theme.palette.error.main} strokeWidth={2} name="7-dniowa średnia" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 4 }}>
                Brak wystarczających danych dla dziennego rozkładu wydatków.
              </Alert>
            )}
            
            {/* Trendy kategorii w ostatnich miesiącach */}
            {categoryTrends && categoryTrends.monthlyData && categoryTrends.monthlyData.length > 0 ? (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Trendy wydatków w kategoriach</Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={categoryTrends.monthlyData} 
                      margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip formatter={(value) => formatAmount(value)} />
                      <Legend />
                      {categoryTrends.categories.map((category, index) => (
                        <Line 
                          key={category}
                          type="monotone" 
                          dataKey={category} 
                          stroke={COLORS[index % COLORS.length]} 
                          strokeWidth={2}
                          activeDot={{ r: 8 }}
                          name={category}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                Brak wystarczających danych do analizy trendów. Potrzebujesz więcej transakcji w różnych miesiącach.
              </Alert>
            )}
          </>
        )}
        
        {/* Zawartość zakładki Analiza i rekomendacje */}
        {tabValue === 3 && (
          <>
            {expenseInsights && expenseInsights.length > 0 ? (
              renderInsightCards()
            ) : (
              <Alert severity="info" sx={{ mb: 4 }}>
                Brak wystarczających danych do generowania wniosków. Dodaj więcej transakcji lub budżetów.
              </Alert>
            )}
            
            {financialHealth ? (
              renderFinancialHealth()
            ) : (
              <Alert severity="info" sx={{ mb: 4 }}>
                Brak wystarczających danych do oceny zdrowia finansowego.
              </Alert>
            )}
            
            {/* Rekomendacje dotyczące budżetów */}
            {categoryStats && categoryStats.length > 0 ? (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Rekomendacje kategorii budżetowych</Typography>
                
                <Grid container spacing={2}>
                  {categoryStats
                    .filter(cat => cat.budgetLimit === 0 || cat.overBudget)
                    .slice(0, 6)
                    .map((category) => (
                      <Grid item xs={12} sm={6} md={4} key={category.name}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            position: 'relative',
                            overflow: 'hidden',
                            height: '100%',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '4px',
                              height: '100%',
                              backgroundColor: category.budgetLimit === 0 
                                ? theme.palette.info.main 
                                : theme.palette.error.main
                            }
                          }}
                        >
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              {category.name}
                            </Typography>
                            
                            {category.budgetLimit === 0 ? (
                              <>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  Brak określonego budżetu
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                  Wydatki: {formatAmount(category.total)}
                                </Typography>
                                <Typography variant="body2">
                                  Zalecenie: Dodaj budżet dla tej kategorii, aby lepiej kontrolować wydatki.
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  Przekroczony budżet
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                  Budżet: {formatAmount(category.budgetLimit)}
                                </Typography>
                                <Typography variant="subtitle2" color="error.main" sx={{ mb: 2 }}>
                                  Przekroczenie: {formatAmount(category.total - category.budgetLimit)} ({(((category.total / category.budgetLimit) - 1) * 100).toFixed(1)}%)
                                </Typography>
                                <Typography variant="body2">
                                  Zalecenie: Zwiększ budżet lub ogranicz wydatki w tej kategorii.
                                </Typography>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            ) : (
              <Alert severity="info">
                Brak kategorii wydatków do analizy. Dodaj więcej transakcji z różnymi kategoriami.
              </Alert>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default ExpenseCategoryAnalysis;